import { restoreElements } from "@excalidraw/excalidraw";
import { getDataURL } from "@excalidraw/excalidraw/data/blob";
import { isFrameLikeElement } from "@excalidraw/excalidraw/element/typeChecks";
import { getElementsOverlappingFrame } from "@excalidraw/excalidraw/frame";
import { IMAGE_MIME_TYPES } from "@canvaschool/contracts";

import { sortFramesForReading } from "../editor/framePdf";

import type {
  ExcalidrawElement,
  ExcalidrawFrameLikeElement,
  FileId,
} from "@excalidraw/excalidraw/element/types";
import type { BinaryFileData, BinaryFiles } from "@excalidraw/excalidraw/types";
import type { CanvasState } from "@canvaschool/contracts";
import type { CanvaschoolApi } from "../api/client";

/** How many images are downloaded at the same time. */
const ASSET_CONCURRENCY = 4;

export type ViewerScene = {
  /** Every element, deleted ones included (the renderer filters them). */
  elements: readonly ExcalidrawElement[];
  /** Live frames in reading order. */
  frames: ExcalidrawFrameLikeElement[];
  assetIds: ReadonlySet<string>;
};

/** The server scene includes deleted elements; only live frames are shown. */
export const prepareScene = (state: CanvasState): ViewerScene => {
  const elements: readonly ExcalidrawElement[] = restoreElements(
    state.scene as unknown as ExcalidrawElement[],
    null,
  );
  return {
    elements,
    frames: sortFramesForReading(
      elements.filter(isFrameLikeElement).filter((frame) => !frame.isDeleted),
    ),
    assetIds: new Set(state.assetIds),
  };
};

/** Stored images the frame's export draws: the renderer's own overlap test,
 * so an unparented image lying across the frame is loaded too. */
export const fileIdsOfFrame = (
  scene: ViewerScene,
  frame: ExcalidrawFrameLikeElement,
): string[] => {
  const ids = new Set<string>();
  const live = scene.elements.filter((element) => !element.isDeleted);
  for (const element of getElementsOverlappingFrame(live, frame)) {
    if (
      element.type === "image" &&
      element.fileId &&
      scene.assetIds.has(element.fileId)
    ) {
      ids.add(element.fileId);
    }
  }
  return [...ids];
};

const isImageMimeType = (value: string): value is BinaryFileData["mimeType"] =>
  (IMAGE_MIME_TYPES as readonly string[]).includes(value);

/**
 * Downloads the files into `files` (shared across frames). A file that fails
 * is skipped, so its image is simply missing from the rendered frame.
 */
export const loadFiles = async (
  api: Pick<CanvaschoolApi, "fetchAsset">,
  route: { classId: string; canvasId: string },
  fileIds: readonly string[],
  files: BinaryFiles,
  isCancelled: () => boolean,
): Promise<void> => {
  const queue = fileIds.filter((id) => !files[id]);
  const worker = async () => {
    for (let id = queue.shift(); id !== undefined; id = queue.shift()) {
      if (isCancelled()) {
        return;
      }
      try {
        const blob = await api.fetchAsset(route.classId, route.canvasId, id);
        if (!isImageMimeType(blob.type)) {
          continue;
        }
        const dataURL = await getDataURL(blob);
        const at = Date.now();
        files[id] = {
          id: id as FileId,
          mimeType: blob.type,
          dataURL,
          created: at,
          lastRetrieved: at,
        };
      } catch {
        // Rendered without this image.
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(ASSET_CONCURRENCY, queue.length) }, worker),
  );
};
